import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import { CreateNewsDto, NewsItem, NewsListResponse, NewsOptions, PublicNewsItem, PublicNewsListResponse, UpdateNewsDto } from '@/types/news';

class NewsService {
  // Public methods
  async getNews(page = 1, limit = 10, unread?: boolean): Promise<PublicNewsListResponse> {
    const response = await api.get<ApiResponse<PublicNewsItem[]>>('/news', {
      params: { page, limit, ...(unread ? { unread: true } : {}) },
    });
    
    // The API returns { success, message, data: [...], meta: {...} }
    const { data } = response;
    return {
      data: data.data,
      meta: data.meta,
    };
  }

  async getNewsById(id: number): Promise<PublicNewsItem> {
    const response = await api.get<ApiResponse<PublicNewsItem>>(`/news/${id}`);
    return unwrapApiResponse<PublicNewsItem>(response);
  }

  async markAsRead(id: number): Promise<void> {
    await api.post(`/news/${id}/read`);
  }

  async markAllAsRead(): Promise<void> {
    await api.post('/news/mark-all-read');
  }

  // Admin methods
  async createNews(data: CreateNewsDto): Promise<NewsItem> {
    const response = await api.post<ApiResponse<NewsItem>>('/admin/news', data);
    return unwrapApiResponse<NewsItem>(response);
  }

  async createTestNews(audience: 'clients' | 'experts' | 'all' = 'all'): Promise<NewsItem> {
    const response = await api.post<ApiResponse<NewsItem>>(`/admin/news/test/${audience}`);
    return unwrapApiResponse<NewsItem>(response);
  }

  async getAdminNewsOptions(): Promise<NewsOptions> {
    const response = await api.get<ApiResponse<NewsOptions>>('/admin/news/options');
    return unwrapApiResponse<NewsOptions>(response);
  }

  async getAdminNews(page = 1, limit = 10, targetAudience?: string): Promise<NewsListResponse> {
    const response = await api.get<ApiResponse<NewsItem[]>>('/admin/news', {
      params: { page, limit, ...(targetAudience ? { targetAudience } : {}) },
    });
    
    const { data } = response;
    return {
      data: data.data,
      meta: data.meta,
    };
  }

  async updateNews(id: number, data: UpdateNewsDto): Promise<NewsItem> {
    const response = await api.patch<ApiResponse<NewsItem>>(`/admin/news/${id}`, data);
    return unwrapApiResponse<NewsItem>(response);
  }

  async deleteNews(id: number): Promise<{ id: number }> {
    const response = await api.delete<ApiResponse<{ id: number }>>(`/admin/news/${id}`);
    return unwrapApiResponse<{ id: number }>(response);
  }
}

export const newsService = new NewsService();
